import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import Layouts from "../../Layouts";
import { FaHeartbeat, FaEye, FaSync } from "react-icons/fa";
import { MdOutlineSevereCold } from "react-icons/md";
import { GiLungs } from "react-icons/gi";
import { TbActivity } from "react-icons/tb";
import IcuWardService from "../../../Services/IcuWardService";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import HimsModuleCards from "../NewDashboard/HimsModuleCards";

const STATUS_STYLE = {
  ADMITTED: "bg-blue-50 text-blue-600 border border-blue-200",
  Admitted: "bg-blue-50 text-blue-600 border border-blue-200",
  Critical: "bg-red-50 text-red-600 border border-red-200",
  CRITICAL: "bg-red-50 text-red-600 border border-red-200",
  "On Ventilator": "bg-amber-50 text-amber-600 border border-amber-200",
  Ventilator: "bg-amber-50 text-amber-600 border border-amber-200",
  VENTILATOR: "bg-amber-50 text-amber-600 border border-amber-200",
  Stable: "bg-green-50 text-green-600 border border-green-200",
  STABLE: "bg-green-50 text-green-600 border border-green-200",
  UNDER_TREATMENT: "bg-yellow-50 text-yellow-600 border border-yellow-200",
  "Under Treatment": "bg-yellow-50 text-yellow-600 border border-yellow-200",
  DISCHARGED: "bg-emerald-50 text-emerald-600 border border-emerald-200",
  Discharged: "bg-emerald-50 text-emerald-600 border border-emerald-200",
};

const CHART_COLORS = {
  indigo: ["#C7D2FE", "#A5B4FC", "#818CF8", "#6366F1", "#4F46E5"],
  red: ["#FECACA", "#FCA5A5", "#F87171", "#EF4444", "#DC2626"],
  amber: ["#FDE68A", "#FCD34D", "#FBBF24", "#F59E0B", "#D97706"],
  green: ["#BBF7D0", "#86EFAC", "#4ADE80", "#22C55E", "#16A34A"],
};

const AVATAR_COLORS = [
  "#6366F1",
  "#EF4444",
  "#F59E0B",
  "#22C55E",
  "#8B5CF6",
  "#06B6D4",
  "#EC4899",
  "#14B8A6",
];

const getAvatarBg = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/* ─── Tiny Bar Sparkline ─── */
const Sparkline = ({ color }) => (
  <div className="flex items-end gap-0.5 h-10">
    {(CHART_COLORS[color] || CHART_COLORS.indigo).map((c, i) => (
      <div
        key={i}
        style={{
          backgroundColor: c,
          height: `${(i + 1) * 20}%`,
          width: "6px",
          borderRadius: "2px",
        }}
      />
    ))}
  </div>
);

export default function ICUWards() {
  const history = useHistory();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [wardFilter, setWardFilter] = useState("All");
  const [date, setDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const storedLogin =
    sessionStorage.getItem("login_result") ||
    localStorage.getItem("login_result");
  const user = storedLogin ? JSON.parse(storedLogin)?.user : null;

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (user?.account_Id) payload.account_Id = user.account_Id;
      if (user?.clinic_Id) payload.clinic_Id = user.clinic_Id;

      const res = await IcuWardService.WardPatientList(payload);
      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.admissionList)
        ? res.data.admissionList
        : [];
      setPatients(data);
    } catch (err) {
      console.error("Failed to fetch ICU ward patient list with params:", err);
      // Fallback: fetch without params in case backend expects an unparameterized endpoint
      try {
        const fallbackRes = await IcuWardService.WardPatientList({});
        const fallbackData = Array.isArray(fallbackRes?.data)
          ? fallbackRes.data
          : Array.isArray(fallbackRes?.data?.data)
          ? fallbackRes.data.data
          : Array.isArray(fallbackRes?.data?.admissionList)
          ? fallbackRes.data.admissionList
          : [];
        setPatients(fallbackData);
      } catch (fallbackErr) {
        console.error("Fallback fetch also failed:", fallbackErr);
        setPatients([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  /* ─── Compute Unique Filter Options ─── */
  const availableWards = useMemo(() => {
    const set = new Set();
    patients.forEach((p) => {
      if (p.wardCode) set.add(p.wardCode);
      if (p.wardBed) set.add(p.wardBed);
    });
    return ["All", ...Array.from(set)];
  }, [patients]);

  const availableStatuses = useMemo(() => {
    const set = new Set();
    patients.forEach((p) => {
      if (p.admissionStatus) set.add(p.admissionStatus);
      if (p.status) set.add(p.status);
    });
    if (set.size === 0) {
      return ["All", "ADMITTED", "Critical", "On Ventilator", "Stable"];
    }
    return ["All", ...Array.from(set)];
  }, [patients]);

  /* ─── Dynamic Stats Calculation ─── */
  const totalCount = patients.length;
  const criticalCount = patients.filter((p) => {
    const s = (p.admissionStatus || p.status || "").toUpperCase();
    const d = (p.diagnosis || "").toUpperCase();
    return s.includes("CRITICAL") || d.includes("CRITICAL");
  }).length;
  const ventilatorCount = patients.filter((p) => {
    const s = (p.admissionStatus || p.status || "").toUpperCase();
    const d = (p.diagnosis || "").toUpperCase();
    return s.includes("VENTILATOR") || d.includes("VENTILATOR");
  }).length;
  const stableCount = patients.filter((p) => {
    const s = (p.admissionStatus || p.status || "").toUpperCase();
    return s === "STABLE" || s === "ADMITTED";
  }).length;

  const statCards = [
    {
      label: "Total ICU Patients",
      value: String(totalCount).padStart(2, "0"),
      icon: <FaHeartbeat className="text-[#6366F1] text-lg" />,
      iconBg: "#EDE9FE",
      chart: "indigo",
    },
    {
      label: "Critical",
      value: String(criticalCount).padStart(2, "0"),
      icon: <MdOutlineSevereCold className="text-[#EF4444] text-lg" />,
      iconBg: "#FEE2E2",
      chart: "red",
    },
    {
      label: "On Ventilator",
      value: String(ventilatorCount).padStart(2, "0"),
      icon: <GiLungs className="text-[#F59E0B] text-lg" />,
      iconBg: "#FEF3C7",
      chart: "amber",
    },
    {
      label: "Stable",
      value: String(stableCount).padStart(2, "0"),
      icon: <TbActivity className="text-[#22C55E] text-lg" />,
      iconBg: "#DCFCE7",
      chart: "green",
    },
  ];

  /* ─── Client Filter Logic ─── */
  const filtered = useMemo(() => {
    return patients.filter((p) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (p.patientName || p.name || "").toLowerCase().includes(q) ||
        (p.patientId || "").toLowerCase().includes(q) ||
        (p.admissionNo || "").toLowerCase().includes(q) ||
        (p.mobile || "").includes(q) ||
        (p.diagnosis || "").toLowerCase().includes(q) ||
        (p.userName || p.doctor || "").toLowerCase().includes(q);

      const statusVal = p.admissionStatus || p.status || "";
      const matchStatus =
        statusFilter === "All" ||
        statusVal.toLowerCase() === statusFilter.toLowerCase();

      const wardVal = p.wardCode || p.wardBed || "";
      const matchWard =
        wardFilter === "All" ||
        wardVal.toLowerCase() === wardFilter.toLowerCase();

      const matchDate =
        !date ||
        (p.admissionDate &&
          (p.admissionDate.includes(date) ||
            dayjs(p.admissionDate).format("YYYY-MM-DD").includes(date) ||
            dayjs(p.admissionDate).format("DD/MM/YYYY").includes(date)));

      return matchSearch && matchStatus && matchWard && matchDate;
    });
  }, [patients, search, statusFilter, wardFilter, date]);

  /* ─── Pagination ─── */
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setWardFilter("All");
    setDate("");
    setCurrentPage(1);
  };

  const handleViewPatient = (patient) => {
    if (patient?.patientId) {
      history.push(
        APP_ROUTES.INPATIENTDETAIL.replace(":id", patient.patientId),
        { admission: patient }
      );
    }
  };

  return (
    <Layouts>
      <div className="p-4">
        {/* ── Common HIMS Module Cards ── */}
        <div className="mb-5">
          <HimsModuleCards
            activeId="icu"
            counts={{
              icu_critical: criticalCount,
              icu_stable: stableCount,
            }}
          />
        </div>

        {/* ── Header ── */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
              style={{ backgroundColor: "#EDE9FE" }}
            >
              <FaHeartbeat className="text-[#6366F1] text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#333333] font-sans">ICU Wards</h1>
              <p className="text-sm text-gray-500 font-sans">
                Manage ICU patients and care details
              </p>
            </div>
          </div>
          <button
            onClick={fetchPatients}
            disabled={loading}
            className="flex items-center gap-2 border bg-white rounded-lg px-3 py-2 text-sm font-sans text-gray-700 hover:bg-gray-50 transition shadow-sm"
            title="Refresh patient list"
          >
            <FaSync className={`w-4 h-4 text-[#189FAA] ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {statCards.map((card) => (
            <div
              key={card.label}
              className="bg-white border rounded-xl shadow-sm p-4 md:flex items-center justify-between"
            >
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: card.iconBg }}
                  >
                    {card.icon}
                  </div>
                  <p className="text-xs font-sans text-gray-500">{card.label}</p>
                </div>
                <p className="text-2xl font-bold text-[#333333] font-sans">
                  {card.value}
                </p>
              </div>
              <Sparkline color={card.chart} />
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="bg-white border rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 border rounded-lg px-3 py-2 flex-1 min-w-[200px]">
            <svg
              className="text-gray-400 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" strokeWidth="2" />
              <path
                d="M21 21l-4.35-4.35"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by patient name, ID, mobile number..."
              className="text-sm font-sans outline-none flex-1 bg-transparent text-gray-700 placeholder-gray-400"
            />
          </div>

          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-[10px] font-sans text-gray-400 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="border rounded-lg px-3 py-2 text-sm font-sans text-gray-700 outline-none bg-white w-full sm:w-auto"
              >
                {availableStatuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-[10px] font-sans text-gray-400 mb-1">
                Ward / Bed
              </label>
              <select
                value={wardFilter}
                onChange={(e) => {
                  setWardFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="border rounded-lg px-3 py-2 text-sm font-sans text-gray-700 outline-none bg-white w-full sm:w-auto"
              >
                {availableWards.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-full sm:w-auto">
              <label className="text-[10px] font-sans text-gray-400 mb-1">
                Date
              </label>
              <div className="border rounded-lg px-3 py-2 flex items-center gap-2 w-full sm:w-auto">
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="18"
                    rx="2"
                    strokeWidth="2"
                  />
                  <path
                    d="M16 2v4M8 2v4M3 10h18"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="text-sm font-sans outline-none bg-transparent text-gray-700 w-full sm:w-auto"
                />
              </div>
            </div>

            <div className="flex items-end gap-2 w-full sm:w-auto">
              <button
                onClick={handleClearFilters}
                className="border bg-white rounded-lg p-2 text-gray-500 hover:bg-gray-50"
                title="Reset Filters"
              >
                <FaSync className="w-4 h-4" />
              </button>
              <button
                onClick={handleClearFilters}
                className="border bg-white rounded-lg px-4 py-2 text-sm font-sans text-[#189FAA] font-semibold hover:bg-teal-50 flex-1 sm:flex-none"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="bg-gray-50 border-b">
                  {[
                    "#",
                    "Patient",
                    "Mobile Number",
                    "Age / Gender",
                    "Primary Diagnosis",
                    "Ward / Bed",
                    "Admission Time",
                    "Doctor",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b animate-pulse">
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-6" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200" />
                          <div className="space-y-1">
                            <div className="h-3 bg-gray-200 rounded w-24" />
                            <div className="h-2 bg-gray-100 rounded w-16" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-3 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-3 bg-gray-200 rounded w-16" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-3 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-3 bg-gray-200 rounded w-16" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-3 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-3 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-6 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-6 bg-gray-200 rounded w-8" />
                      </td>
                    </tr>
                  ))
                ) : paginatedList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-10 text-center text-gray-400 text-sm"
                    >
                      No ICU patients found.
                    </td>
                  </tr>
                ) : (
                  paginatedList.map((p, idx) => {
                    const patientName =
                      p.patientName || p.name || "Unknown Patient";
                    const patientId = p.patientId || p.admissionNo || "-";
                    const mobile = p.mobile || "-";
                    const ageGender =
                      `${p.age ? `${p.age} Y` : ""} ${
                        p.gender
                          ? `/ ${
                              p.gender === "F"
                                ? "Female"
                                : p.gender === "M"
                                ? "Male"
                                : p.gender
                            }`
                          : ""
                      }`.trim() ||
                      p.ageGender ||
                      "-";
                    const diagnosis = p.diagnosis || "-";
                    const wardBed =
                      p.wardName && (p.bedNumber || p.bedCode)
                        ? `${p.wardName} / Bed ${p.bedNumber || p.bedCode}`
                        : p.wardName || p.wardBed || "-";
                    const admissionTime = p.admissionDate
                      ? dayjs(p.admissionDate).isValid()
                        ? dayjs(p.admissionDate).format("DD/MM/YYYY\nhh:mm A")
                        : p.admissionDate
                      : p.admissionTime || "-";
                    const doctorName = p.userName
                      ? p.userName.startsWith("Dr.")
                        ? p.userName
                        : `Dr. ${p.userName}`
                      : p.doctor || "-";
                    const doctorRole =
                      p.doctorRole ||
                      p.assignedDoctorId ||
                      p.doctorId ||
                      "Intensivist";
                    const status = p.admissionStatus || p.status || "ADMITTED";
                    const statusClass =
                      STATUS_STYLE[status] ||
                      STATUS_STYLE[status.toUpperCase()] ||
                      "bg-gray-100 text-gray-600 border border-gray-200";
                    const avatarLetter = (
                      patientName.charAt(0) || "P"
                    ).toUpperCase();
                    const avatarBg = p.avatarBg || getAvatarBg(patientName);

                    return (
                      <tr
                        key={p.admissionId || p.id || idx}
                        className="border-b hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-500">
                          {(currentPage - 1) * pageSize + idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ backgroundColor: avatarBg }}
                            >
                              {avatarLetter}
                            </div>
                            <div>
                              <p className="font-semibold text-[#333333] text-xs">
                                {patientName}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {patientId}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {mobile}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {ageGender}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {diagnosis}
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">
                          {wardBed}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-pre-line text-xs">
                          {admissionTime}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                              {(doctorName.replace(/^Dr\.\s*/i, "").charAt(0) || "D").toUpperCase()}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-[#333333]">
                                {doctorName}
                              </p>
                             
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-md text-xs font-semibold ${statusClass}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleViewPatient(p)}
                            className="p-1.5 bg-white text-gray-500 hover:text-[#189FAA] hover:bg-teal-50 rounded-md transition"
                            title="View Patient Details"
                          >
                            <FaEye className="w-4 h-4 text-[#189FAA]" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="px-4 py-3 border-t flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-gray-500 font-sans">
              Showing{" "}
              {filtered.length === 0
                ? 0
                : (currentPage - 1) * pageSize + 1}{" "}
              to {Math.min(currentPage * pageSize, filtered.length)} of{" "}
              {filtered.length} entries
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ‹ Prev
              </button>
              <span className="text-xs text-gray-600 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border rounded-md text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next ›
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded-md px-2 py-1 text-xs text-gray-600 outline-none bg-white ml-2"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </Layouts>
  );
}
